import { Box, Divider, Typography } from '@mui/material';
import CustomAddTaskForm from 'components/shared/CustomAddTask/CustomAddTaskForm/CustomAddTaskForm';
import CustomDrawer from 'components/shared/CustomDrawer/CustomDrawer';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useInput } from 'customHook/useInput';
import useTaskFileUploader from 'customHook/useTaskFileUpload';
import dayjs, { Dayjs } from 'dayjs';
import { useLocation } from 'react-router-dom';
import { ClientService } from 'service/client.service';
import { HuddleClientService } from 'service/huddle-client.service';
import { TaskService } from 'service/task.service';
import zeplinColor from 'theme/Color';
import validator from 'validator';
import style from './AddTaskForm.module.css';

type AddTaskFormProps = {
  drawerState: boolean;
  onClose: () => void;
  taskType: string;
  onTaskAdded?: () => void;
  initialStatus?: string;
};

type SelectedMember = {
  name?: string;
  id?: string;
};

type Assignee = {
  Name: string;
  Id: string;
  Email?: string;
};

type SelectedItems = {
  priority: string;
  status: string;
  related: { Name: string; Id: string }[];
  assignTo: Assignee[];
  name: { Name: string; Id: string }[];
  payee: { Name: string; Id: string }[];
  nameType: string[];
  relatedType: string[];
  dueDate: string;
  terms: string[];
};

const EMPTY_ITEM = { Name: '', Id: '' };
const DEFAULT_PRIORITY = ['Medium', 'Low', 'High'];
const DEFAULT_STATUS = ['To Do', 'In Progress', 'Done'];

const AddTaskForm = ({
  drawerState,
  onClose,
  taskType,
  onTaskAdded,
  initialStatus,
}: AddTaskFormProps) => {
  const location = useLocation();
  const details = useMemo(() => location.pathname?.split('/') || [], [location.pathname]);
  const Email = useMemo(() => sessionStorage.getItem('current-user') || '', []);

  const selectedMember = useMemo<SelectedMember>(() => {
    const storedMember = sessionStorage.getItem('selected-member');
    return storedMember ? JSON.parse(storedMember) : {};
  }, []);

  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [formLoading, setFormLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [attachments, setAttachments] = useState<{
    removed: string[];
    added: string[];
  }>({
    removed: [],
    added: [],
  });

  const {
    handleChange: handleCommentChange,
    handleBlur: handleCommentBlur,
    clearInput: clearComment,
    value: comment,
    invalid: invalidComment,
    didChange: didCommentChange,
  } = useInput('', () => true);

  const {
    handleChange: handleTermsChange,
    handleBlur: handleTermsBlur,
    clearInput: clearTerms,
    value: terms,
    invalid: invalidTerms,
    didChange: didTermsChange,
  } = useInput('', () => true);

  const {
    handleChange: handleEmailChange,
    handleBlur: handleEmailBlur,
    clearInput: clearEmail,
    value: email,
    invalid: invalidEmail,
    didChange: didEmailChange,
  } = useInput('', validator.isEmail);

  const {
    handleChange: handleLinkChange,
    handleBlur: handleLinkBlur,
    clearInput: clearLink,
    value: link,
    invalid: invalidLink,
    didChange: didLinkChange,
  } = useInput('', () => true);

  const {
    handleChange: handlePayeeChange,
    handleBlur: handlePayeeBlur,
    clearInput: clearPayee,
    value: payee,
    invalid: invalidPayee,
    didChange: didPayeeChange,
  } = useInput('', () => true);

  const {
    handleChange: handleQTYChange,
    handleBlur: handleQTYBlur,
    clearInput: clearQTY,
    value: qTY,
    invalid: invalidQTY,
    didChange: didQTYChange,
  } = useInput('', () => true);

  const {
    handleChange: handleRateChange,
    handleBlur: handleRateBlur,
    clearInput: clearRate,
    value: rate,
    invalid: invalidRate,
    didChange: didRateChange,
  } = useInput('', () => true);

  const {
    handleChange: handleSubjectChange,
    handleBlur: handleSubjectBlur,
    clearInput: clearSubject,
    value: subject,
    invalid: invalidSubject,
    didChange: didSubjectChange,
  } = useInput('', () => true);

  const [subTask] = useState<{ subject: string; comment: string; link: string }[]>([]);

  const initialSelectedItems = useMemo<SelectedItems>(
    () => ({
      priority: '',
      status: initialStatus || '',
      related: [EMPTY_ITEM],
      relatedType: details[1] === 'clients' ? ['Account'] : [''],
      name: [EMPTY_ITEM],
      payee: [EMPTY_ITEM],
      nameType: [''],
      assignTo:
        details[1] === 'team' && selectedMember?.name && selectedMember?.id
          ? [{ Name: selectedMember.name, Id: selectedMember.id }]
          : [EMPTY_ITEM],
      dueDate: '',
      terms: [''],
    }),
    [details, selectedMember, initialStatus],
  );

  const [selectedItems, setSelectedItems] = useState<SelectedItems>(initialSelectedItems);
  const [relatedTo, setRelatedTo] = useState({ account: [EMPTY_ITEM], deal: [EMPTY_ITEM] });
  const [name, setName] = useState({ contact: [EMPTY_ITEM], lead: [EMPTY_ITEM] });
  const [assign, setAssign] = useState<Assignee[]>([EMPTY_ITEM]);
  const [loading, setLoading] = useState(false);
  const [Priority, setPriority] = useState<string[]>(DEFAULT_PRIORITY);
  const [Status, setStatus] = useState<string[]>(DEFAULT_STATUS);

  useEffect(() => {
    if (drawerState) {
      if (initialStatus) {
        setSelectedItems((prev) => ({ ...prev, status: initialStatus }));
      }
      if (!date) {
        setDate(dayjs());
      }
    }
  }, [initialStatus, drawerState, date]);

  const getDetails = useCallback(
    async (id: string) => {
      if (!id) return;
      setLoading(true);
      try {
        const res =
          details[2] === 'huddle'
            ? await HuddleClientService.getClientHuddleDetails(id)
            : await ClientService.getClientDetails(id);

        setSelectedItems((prev) => ({
          ...prev,
          related: [{ Name: res?.accountName || '', Id: res?.accountId || '' }],
        }));
      } catch (err) {
        console.log(err, 'errdeal');
      } finally {
        setLoading(false);
      }
    },
    [details],
  );

  const getDropDown = useCallback(async () => {
    setLoading(true);
    try {
      const res = await TaskService.getTaskDropdowns();
      setRelatedTo(res?.relatedTo || { account: [EMPTY_ITEM], deal: [EMPTY_ITEM] });
      setAssign(res?.assignTo || [EMPTY_ITEM]);
      setName({
        contact: res?.nameContact || [EMPTY_ITEM],
        lead: res?.nameLead || [EMPTY_ITEM],
      });
      if (res?.priorityOrder) {
        setPriority(Object.keys(res.priorityOrder));
      }
      if (res?.recordStatusTypes) {
        setStatus(Object.keys(res.recordStatusTypes));
      }
    } catch (err) {
      console.log(err);
      setRelatedTo({ account: [EMPTY_ITEM], deal: [EMPTY_ITEM] });
      setAssign([EMPTY_ITEM]);
      setName({ contact: [EMPTY_ITEM], lead: [EMPTY_ITEM] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (details.length > 2) {
      if (details[2] === 'huddle' && details[3]) {
        getDetails(details[3]);
      } else if (details[1] === 'clients' && details[2]) {
        getDetails(details[2]);
      }
    }
    getDropDown();
  }, [details, getDetails, getDropDown]);

  const handleSelection = useCallback((item: unknown, type: string) => {
    const key = type as keyof SelectedItems;
    setSelectedItems((prev) => {
      if (Array.isArray(item)) {
        return { ...prev, [key]: [...item] };
      }
      if (item && typeof item === 'object' && 'target' in item) {
        const value = (item as { target: { value: unknown } }).target.value;
        return {
          ...prev,
          [key]: typeof value === 'string' ? value.split(',') : [value],
        };
      }
      return { ...prev, [key]: [item] };
    });
  }, []);

  const handleDeleteItems = useCallback((item: Assignee, type: string) => {
    const key = type as keyof SelectedItems;
    setSelectedItems((prev) => ({
      ...prev,
      [key]: prev.assignTo.filter((val) => val !== item),
    }));
  }, []);

  const { uploadTaskFiles } = useTaskFileUploader();

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
      try {
        const uploadedUrls = await uploadTaskFiles(acceptedFiles, Email);
        if (uploadedUrls.length > 0) {
          setAttachments((prev) => ({
            ...prev,
            added: [...prev.added, ...uploadedUrls],
          }));
        }
      } catch (err) {
        console.error('Error uploading files:', err);
      }
    },
    [Email, uploadTaskFiles],
  );

  const handleDeleteFiles = useCallback((index: number) => {
    setSelectedFiles((prev) => prev.filter((_, ind) => ind !== index));
    setAttachments((prev) => {
      const fileToRemove = prev.added[index];
      return {
        added: prev.added.filter((_, ind) => ind !== index),
        removed: fileToRemove ? [...prev.removed, fileToRemove] : prev.removed,
      };
    });
  }, []);

  const formatAssignee = useCallback(
    (assignees: Assignee[], assignList: Assignee[]): Assignee[] | undefined => {
      const validAssignees = assignees.filter((assignee) => assignee?.Id);
      if (validAssignees.length === 0) return undefined;

      return validAssignees.map((selectedAssignee) => {
        const fullAssignee = assignList.find((a) => a?.Id === selectedAssignee.Id);
        const assigneeObj = fullAssignee || selectedAssignee;
        return {
          Name: assigneeObj?.Name || selectedAssignee?.Name || '',
          Id: assigneeObj?.Id || selectedAssignee?.Id || '',
          Email: assigneeObj?.Email || selectedAssignee?.Email || '',
        };
      });
    },
    [],
  );

  const resetForm = useCallback(() => {
    clearComment();
    clearLink();
    clearSubject();
    clearEmail();
    clearQTY();
    clearRate();
    clearPayee();
    setSelectedFiles([]);
    setAttachments({ removed: [], added: [] });
    setDate(dayjs());
    setSelectedItems({
      priority: '',
      status: initialStatus || '',
      related: [EMPTY_ITEM],
      relatedType: details[1] === 'clients' ? ['Account'] : [''],
      name: [EMPTY_ITEM],
      payee: [EMPTY_ITEM],
      nameType: [''],
      assignTo:
        details[1] === 'team' && selectedMember?.name && selectedMember?.id
          ? [{ Name: selectedMember.name, Id: selectedMember.id }]
          : [EMPTY_ITEM],
      dueDate: '',
      terms: [''],
    });
  }, [
    clearComment,
    clearLink,
    clearSubject,
    clearEmail,
    clearQTY,
    clearRate,
    clearPayee,
    initialStatus,
    details,
    selectedMember,
  ]);

  const addTask = useCallback(async () => {
    try {
      setFormLoading(true);
      setError('');

      const formattedAssignee = formatAssignee(selectedItems.assignTo, assign);

      const addProps = {
        subject,
        recordTypeName: taskType,
        ...(date && { dueDate: date.toISOString() }),
        ...(comment && { description: comment }),
        ...(selectedItems.related[0]?.Id && {
          relatedTo: {
            Id: selectedItems.related[0].Id,
            Name: selectedItems.related[0].Name || '',
          },
        }),
        ...(selectedItems.relatedType[0] && {
          relatedType:
            selectedItems.relatedType[0].charAt(0).toUpperCase() +
            selectedItems.relatedType[0].slice(1),
        }),
        status: Array.isArray(selectedItems.status)
          ? selectedItems.status[0]
          : selectedItems.status,
        priority: Array.isArray(selectedItems.priority)
          ? selectedItems.priority[0]
          : selectedItems.priority,
        ...(selectedItems.name[0]?.Id && {
          name: {
            Id: selectedItems.name[0].Id,
            Name: selectedItems.name[0].Name || '',
          },
        }),
        ...(selectedItems.nameType[0] && {
          nameType:
            selectedItems.nameType[0].charAt(0).toUpperCase() + selectedItems.nameType[0].slice(1),
        }),
        ...(link && { link }),
        ...((attachments.added.length || attachments.removed.length) && { attachments }),
        ...(formattedAssignee && { assignedTo: formattedAssignee }),
      };

      await TaskService.createTask(addProps);
      resetForm();
      onClose();
      onTaskAdded?.();
    } catch (err: unknown) {
      const errorMessage =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      setError(errorMessage || 'Failed to create task');
    } finally {
      setFormLoading(false);
    }
  }, [
    selectedItems,
    assign,
    subject,
    taskType,
    date,
    comment,
    link,
    attachments,
    formatAssignee,
    resetForm,
    onClose,
    onTaskAdded,
  ]);

  const handleDrawerClose = useCallback(
    (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      onClose();
    },
    [onClose],
  );

  const OnClickSubTask = useCallback(() => {}, []);

  return (
    <CustomDrawer state={drawerState} onClose={handleDrawerClose}>
      <Box sx={{ width: { xs: '427px', xl: '527px' }, position: 'relative', overflowY: 'scroll' }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            backgroundColor: zeplinColor.Neutral1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '40px 24px 24px 24px',
            }}
          >
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <Typography variant='responsiveHeading2'>New Task: {taskType}</Typography>
            </Box>
            <img
              src='/assets/close-icon.svg'
              className={style.icon}
              alt='close'
              style={{ cursor: 'pointer' }}
              onClick={onClose}
            />
          </Box>

          <Divider
            component='div'
            sx={{ borderColor: zeplinColor.Neutral5, position: 'relative' }}
          />
        </Box>

        <CustomAddTaskForm
          subtask={false}
          OnClickSubtask={OnClickSubTask}
          payee={payee}
          handlePayeeChange={handlePayeeChange}
          handlePayeeBlur={handlePayeeBlur}
          invalidPayee={invalidPayee}
          didPayeeChange={didPayeeChange}
          rate={rate}
          didRateChange={didRateChange}
          handleRateChange={handleRateChange}
          invalidRate={invalidRate}
          handleRateBlur={handleRateBlur}
          handleQTYChange={handleQTYChange}
          handleQTYBlur={handleQTYBlur}
          qTY={qTY}
          didQTYChange={didQTYChange}
          invalidQTY={invalidQTY}
          handleCommentChange={handleCommentChange}
          handleCommentBlur={handleCommentBlur}
          comment={comment}
          invalidComment={invalidComment}
          didCommentChange={didCommentChange}
          handleLinkChange={handleLinkChange}
          handleLinkBlur={handleLinkBlur}
          link={link}
          invalidLink={invalidLink}
          didLinkChange={didLinkChange}
          handleSubjectChange={handleSubjectChange}
          handleSubjectBlur={handleSubjectBlur}
          subject={subject}
          invalidSubject={invalidSubject}
          didSubjectChange={didSubjectChange}
          error={error}
          onClearError={() => setError('')}
          Priority={Priority}
          Status={Status}
          selectedItems={selectedItems}
          handleSelection={handleSelection}
          onDrop={onDrop}
          selectedFiles={selectedFiles}
          add={addTask}
          deleteFile={handleDeleteFiles}
          selectedType={taskType}
          subTask={subTask}
          date={date}
          setDate={setDate}
          handleDeleteItems={handleDeleteItems}
          Assign={assign}
          relatedTo={relatedTo}
          name={name}
          edit={false}
          key={`${assign?.[0]?.Id || 'new-task'}-${initialStatus || ''}`}
          email={email}
          handleEmailChange={handleEmailChange}
          handleEmailBlur={handleEmailBlur}
          didEmailChange={didEmailChange}
          invalidEmail={invalidEmail}
          handleTermsChange={handleTermsChange}
          handleTermsBlur={handleTermsBlur}
          terms={terms}
          invalidTerms={invalidTerms}
          didTermsChange={didTermsChange}
          formLoading={formLoading}
          setFormLoading={setFormLoading}
        />
      </Box>
    </CustomDrawer>
  );
};

export default AddTaskForm;
